import React, { Component } from "react";
import Dismissable from "@streeteasy/design-system/legacy/Dismissable";
import { bool, func, number } from "prop-types";
import { uncontrollable } from "uncontrollable";

/**
 * A component which renders a `Dismissable` and closes it after a specified
 * time. Like `Dismissable`, it can be controlled or uncontrolled. Accepts all
 * of `Dismissable`'s props.
 *
 */
export class TimedDismissable extends Component {
  static propTypes = {
    ...Dismissable.ControlledComponent.propTypes,
    /**
     * Whether the modal should be visible or not.
     */
    isOpen: bool,
    /**
     * Callback function for when the instance closes.
     *
     */
    onClosed: func,
    /**
     * Callback function for when the close button is clicked. If provided,
     * this instance's parent is responsible for updating `isOpen` accordingly.
     *
     * @param {bool} isOpen
     */
    onToggle: func,
    /**
     * Time in milliseconds that the component should stay visible for,
     * defaults to 5000.
     */
    time: number
  };

  static defaultProps = {
    ...Dismissable.ControlledComponent.defaultProps,
    isOpen: true,
    onClosed: () => {},
    onToggle: () => {},
    time: 5000
  };

  componentDidMount() {
    const { isOpen } = this.props;

    if (isOpen) {
      this.startTimer();
    }
  }

  componentDidUpdate(prevProps) {
    const { isOpen, onClosed } = this.props;
    const { isOpen: wasOpen } = prevProps;

    if (isOpen && !wasOpen) {
      this.startTimer();
    } else if (!isOpen && wasOpen) {
      this.stopTimer();
      onClosed();
    }
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  startTimer() {
    const { onClosed, onToggle, time } = this.props;

    this.timeoutId = setTimeout(() => {
      onToggle(false);
      onClosed();
      this.timeoutId = null;
    }, time);
  }

  stopTimer() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  handleToggle = isOpen => {
    const { onToggle } = this.props;
    onToggle(isOpen);
  };

  render() {
    const {
      isOpen,
      onClosed,
      onToggle,
      time,
      ...passThroughProps
    } = this.props;

    return (
      <Dismissable
        {...passThroughProps}
        isOpen={isOpen}
        onToggle={this.handleToggle}
      />
    );
  }
}

export default uncontrollable(TimedDismissable, { isOpen: "onToggle" });
